var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import icon from 'jimu-icons/svg/outlined/editor/sum.svg';
import { rem } from 'polished';
import * as React from 'react';
import { useIntl } from 'react-intl';
import Immutable from 'seamless-immutable';
import { AbstractDataAction, DataLevel } from '../base-data-action';
import { DataSourceStatus, DataSourceTypes } from '../data-sources';
import { loadModule } from '../module-loader';
import defaultMessages from '../translations/default';
import { JimuFieldType, LoadingType } from '../types/common';
import { getFieldStatistics, getStatisticalFields, queryFieldStatistics, statTypes } from './data-statistics-util';
import wrongOutlined from 'jimu-icons/svg/outlined/suggested/wrong.svg';
import { useTranslation } from '../hooks';
export default class DataStatistics extends AbstractDataAction {
    constructor(props) {
        super(props);
        this.dataStatisticsPopperRef = {};
        this.isWatchingDataSetsChange = true;
        this.icon = icon;
    }
    loadModules() {
        return __awaiter(this, void 0, void 0, function* () {
            const { FloatingPanel, Label, Loading, Icon } = yield import('jimu-ui');
            this._modules = {
                FloatingPanel,
                Label,
                Loading,
                Icon
            };
        });
    }
    isSupported(dataSets, dataLevel) {
        return __awaiter(this, void 0, void 0, function* () {
            const isSupported = dataSets.length === 1 && isSupportStatistics(dataSets[0], dataLevel);
            if (isSupported && !this._modules) {
                yield this.loadModules();
            }
            return isSupported;
        });
    }
    onExecute(dataSets, dataLevel, widgetId, actionConfig) {
        if (!this._version) {
            this._version = 0;
        }
        const props = Object.assign({ version: this._version++, iconRef: document.querySelector('.active-data-action-item') || {
                getBoundingClientRect: () => ({
                    width: 0,
                    height: 0,
                    left: document.body.clientWidth / 2,
                    top: document.body.clientHeight / 2 - DEFAULT_SIZE.height / 2
                }),
                isVirtualElement: true
            }, dataSet: dataSets[0], dataLevel }, this._modules);
        this.dataStatisticsPopperRef[widgetId] = React.createRef();
        return Promise.resolve(jsx(DataStatisticsPopper, Object.assign({ ref: this.dataStatisticsPopperRef[widgetId] }, props)));
    }
    onDataSetsChange(dataSets, widgetId) {
        var _a, _b, _c;
        const dataStatisticsPopperInstance = (_a = this.dataStatisticsPopperRef[widgetId]) === null || _a === void 0 ? void 0 : _a.current;
        if (dataSets.length === 1 && dataStatisticsPopperInstance) {
            (_c = (_b = this.dataStatisticsPopperRef[widgetId].current).updateDataSet) === null || _c === void 0 ? void 0 : _c.call(_b, dataSets[0]);
        }
    }
}
const MIN_SIZE = { width: 335, height: 300 };
const DEFAULT_SIZE = { width: 350, height: 320 };
const popperStyle = css `
  .component-field-selector {
    .ds-name {
      padding: 0 !important;
      margin-bottom: 8px;
      .option-label {
        display: none;
      }
      .mt-2 {
        display: none;
      }
    }
  }
`;
const DataStatisticsPopper = React.forwardRef((props, ref) => {
    const { FloatingPanel, Label, Loading, Icon, version, iconRef, dataSet: propDataSet, dataLevel } = props;
    const [dataSet, setDataSet] = React.useState(propDataSet);
    const updateDataSet = React.useCallback((newDataSet) => {
        const oldRecordIds = dataSet.records.map(r => r.getId()).join(',');
        const newRecordIds = newDataSet.records.map(r => r.getId()).join(',');
        if (oldRecordIds !== newRecordIds) {
            setDataSet(newDataSet);
        }
    }, [dataSet.records]);
    React.useImperativeHandle(ref, () => {
        return { updateDataSet, dataLevel };
    }, [dataLevel, updateDataSet]);
    const translate = useTranslation(defaultMessages);
    const fieldSelectorRef = React.useRef(null);
    const [moduleLoaded, setModuleLoaded] = React.useState(false);
    React.useEffect(() => {
        loadFieldSelector();
    }, []);
    const loadFieldSelector = () => __awaiter(void 0, void 0, void 0, function* () {
        const { FieldSelector } = yield import('jimu-ui/advanced/data-source-selector');
        fieldSelectorRef.current = FieldSelector;
        setModuleLoaded(true);
    });
    const [size, setSize] = React.useState(DEFAULT_SIZE);
    const [isOpen, setIsOpen] = React.useState(true);
    React.useEffect(() => {
        setIsOpen(true);
    }, [version]);
    const handleClose = () => {
        setIsOpen(false);
    };
    const handleToggle = (evt, type) => {
        if (type !== 'clickOutside') {
            handleClose();
        }
    };
    const handleResize = (size) => {
        setSize(size);
    };
    const dataSources = [dataSet.dataSource];
    const fieldTypes = Immutable([JimuFieldType.Number]);
    const [selectedFields, setSelectedFields] = React.useState(Immutable([]));
    const [statResult, setStatResult] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const intl = useIntl();
    const preFieldName = React.useRef(null);
    const executeStatistics = React.useCallback((dataSet, fieldName) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            let result;
            if (dataLevel === DataLevel.DataSource) {
                if (preFieldName.current === fieldName)
                    return;
                result = yield queryFieldStatistics(dataSet.dataSource, fieldName);
                preFieldName.current = fieldName;
            }
            else if (dataLevel === DataLevel.Records) {
                result = getFieldStatistics(dataSet.records, fieldName);
            }
            const newStatResult = {};
            statTypes.forEach(statType => {
                const val = result[statType];
                if (typeof val === 'number') {
                    newStatResult[statType] = intl.formatNumber(val);
                }
            });
            setStatResult(newStatResult);
        }
        catch (e) {
            setStatResult(null);
        }
        finally {
            setLoading(false);
        }
    }), [dataLevel, intl]);
    const onChangeFields = React.useCallback((allSelectedFields, ds) => {
        var _a;
        const fieldName = (_a = allSelectedFields === null || allSelectedFields === void 0 ? void 0 : allSelectedFields[0]) === null || _a === void 0 ? void 0 : _a.name;
        setSelectedFields(Immutable([fieldName]));
    }, []);
    const hiddenFields = React.useMemo(() => {
        var _a;
        const statisticalFields = getStatisticalFields(dataSet);
        const allFields = Object.values(((_a = dataSet.dataSource.getSchema()) === null || _a === void 0 ? void 0 : _a.fields) || {}).map(f => f.name);
        const hiddenFields = allFields.filter(fieldName => !statisticalFields.includes(fieldName));
        return Immutable(hiddenFields);
    }, [dataSet]);
    const FieldSelector = fieldSelectorRef.current;
    React.useEffect(() => {
        const fieldName = selectedFields[0];
        if (!dataSet || !fieldName)
            return;
        executeStatistics(dataSet, fieldName);
    }, [dataSet, executeStatistics, selectedFields]);
    return jsx(FloatingPanel, { version: version, open: isOpen, headerTitle: translate('dataAction_DataStatistics'), onHeaderClose: handleClose, minSize: MIN_SIZE, onResize: handleResize, dragBounds: "body", defaultSize: size, reference: iconRef, toggle: handleToggle, className: 'data-statistics-popper', css: popperStyle },
        jsx("div", { className: "h-100 p-4 position-relative overflow-auto" }, !moduleLoaded
            ? jsx(Loading, { type: LoadingType.Secondary })
            : jsx(React.Fragment, null,
                jsx(Label, { css: css `
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `, title: dataSources[0].getLabel(), "aria-label": dataSources[0].getLabel() }, dataSources[0].getLabel()),
                jsx(FieldSelector, { dataSources: dataSources, types: fieldTypes, selectedFields: selectedFields, hiddenFields: hiddenFields, useDropdown: true, isDataSourceDropDownHidden: dataSources.length === 1, placeholder: translate('pleaseSelectAField'), className: "h-auto mb-4", onChange: onChangeFields }),
                statResult && !loading && jsx("div", null, Object.keys(statResult).map((statType) => jsx(Label, { className: 'd-flex justify-content-between', key: statType },
                    jsx("span", { css: css `
              width: ${rem(150)};
              flex-shrink: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            ` }, translate(`${statType}Label`)),
                    jsx("span", { css: css `
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `, title: statResult[statType] }, statResult[statType])))),
                !statResult && !loading && jsx(Label, null,
                    jsx(Icon, { icon: wrongOutlined, color: 'var(--sys-color-error-main)' }),
                    jsx("span", { className: 'ml-2' }, translate('error'))),
                loading && jsx(Loading, { type: LoadingType.Secondary }))));
});
const supportedDsTypes = [
    DataSourceTypes.FeatureLayer,
    DataSourceTypes.SceneLayer,
    DataSourceTypes.BuildingComponentSubLayer,
    DataSourceTypes.OrientedImageryLayer,
    DataSourceTypes.ImageryLayer,
    DataSourceTypes.SubtypeGroupLayer,
    DataSourceTypes.SubtypeSublayer
];
const isSupportStatistics = (dataSet, dataLevel) => {
    const statisticalFields = getStatisticalFields(dataSet);
    let isSupport = supportedDsTypes.includes(dataSet.dataSource.type) &&
        statisticalFields.length > 0 &&
        dataSet.dataSource.getStatus() !== DataSourceStatus.NotReady;
    if (!isSupport)
        return isSupport;
    let supportsStatistics = false;
    if (dataLevel === DataLevel.DataSource && 'getCapabilities' in dataSet.dataSource) {
        const ds = dataSet.dataSource;
        supportsStatistics = ds.getCapabilities().getQueryCapabilities().supportsStatistics;
    }
    if ((dataLevel === DataLevel.Records && dataSet.records.length === 0) ||
        (dataLevel === DataLevel.DataSource && !supportsStatistics)) {
        isSupport = false;
    }
    return isSupport;
};
